import { useCallback, useEffect, useState } from "react";
import { categories as mockCategories } from "@/mocks/products";
import { useSessionContext } from "@/integrations/supabase/SessionContextProvider";
import {
  fetchSupabaseCollection,
  loadCachedValue,
  replaceSupabaseCollection,
  saveCachedValue,
  subscribeToCachedValue,
} from "@/integrations/supabase/contentStore";

const STORAGE_KEY = "afiliabr_admin_categories";

export type AdminCategory = {
  id: number;
  name: string;
  slug: string;
  icon: string;
  count: number;
  image: string;
  subcategories: string[];
  visible: boolean;
  order: number;
};

type CategoryRow = {
  id: number | string;
  name: string;
  slug: string;
  icon: string;
  count: number | string;
  image: string;
  subcategories: string[] | null;
  visible: boolean;
  display_order: number | string;
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

const defaultCategories: AdminCategory[] = mockCategories.map((category, index) => ({
  id: category.id,
  name: category.name,
  slug: category.slug,
  icon: category.icon,
  count: category.count,
  image: category.image,
  subcategories: category.subcategories,
  visible: true,
  order: index,
}));

function normalizeCategory(category: Partial<AdminCategory>, index = 0): AdminCategory {
  return {
    id: Number(category.id ?? 0),
    name: category.name?.trim() ?? "",
    slug: category.slug?.trim() || slugify(category.name ?? ""),
    icon: category.icon?.trim() ?? "",
    count: Number(category.count ?? 0),
    image: category.image?.trim() ?? "",
    subcategories: Array.isArray(category.subcategories)
      ? Array.from(new Set(category.subcategories.map((item) => item.trim()).filter(Boolean)))
      : [],
    visible: Boolean(category.visible),
    order: Number(category.order ?? index),
  };
}

function normalizeCategories(data: Partial<AdminCategory>[]) {
  return data
    .map((category, index) => normalizeCategory(category, index))
    .sort((first, second) => first.order - second.order)
    .map((category, index) => ({ ...category, order: index }));
}

function mapRow(row: CategoryRow): AdminCategory {
  return normalizeCategory({
    id: Number(row.id),
    name: row.name,
    slug: row.slug,
    icon: row.icon,
    count: Number(row.count ?? 0),
    image: row.image,
    subcategories: Array.isArray(row.subcategories) ? row.subcategories : [],
    visible: row.visible,
    order: Number(row.display_order ?? 0),
  });
}

function mapToRow(category: AdminCategory) {
  return {
    id: category.id,
    name: category.name,
    slug: category.slug,
    icon: category.icon,
    count: category.count,
    image: category.image,
    subcategories: category.subcategories,
    visible: category.visible,
    display_order: category.order,
  };
}

function load() {
  return normalizeCategories(loadCachedValue<AdminCategory[]>(STORAGE_KEY, defaultCategories));
}

function save(data: AdminCategory[]) {
  saveCachedValue(STORAGE_KEY, normalizeCategories(data));
}

async function fetchFromSupabase() {
  const rows = await fetchSupabaseCollection<CategoryRow>("categories", "display_order");
  return rows.map((row) => mapRow(row));
}

async function syncToSupabase(categories: AdminCategory[]) {
  const rows = await replaceSupabaseCollection({
    table: "categories",
    rows: normalizeCategories(categories).map((category) => mapToRow(category)),
    orderBy: "display_order",
  });

  return (rows as CategoryRow[]).map((row) => mapRow(row));
}

export function useAdminCategories() {
  const { session } = useSessionContext();
  const [categories, setCategories] = useState<AdminCategory[]>(load);

  useEffect(() => {
    return subscribeToCachedValue(STORAGE_KEY, defaultCategories, (value) => {
      setCategories(normalizeCategories(value as AdminCategory[]));
    });
  }, []);

  useEffect(() => {
    let active = true;

    const sync = async () => {
      try {
        const remote = await fetchFromSupabase();
        if (!active) return;

        if (remote.length > 0) {
          setCategories(remote);
          save(remote);
          return;
        }

        const cached = load();
        if (!session || cached.length === 0) return;

        const seeded = await syncToSupabase(cached);
        if (!active) return;

        setCategories(seeded);
        save(seeded);
      } catch {
        // ignore and keep cache fallback
      }
    };

    void sync();

    return () => {
      active = false;
    };
  }, [session]);

  const persist = useCallback((data: AdminCategory[]) => {
    const normalized = normalizeCategories(data);
    setCategories(normalized);
    save(normalized);

    void syncToSupabase(normalized).then((remote) => {
      setCategories(remote);
      save(remote);
    }).catch(() => {
      // ignore and keep cache fallback
    });
  }, []);

  const addCategory = useCallback((category: Omit<AdminCategory, "id" | "order">) => {
    const newId = Math.max(0, ...categories.map((item) => item.id)) + 1;
    persist([...categories, { ...category, id: newId, order: categories.length }]);
  }, [categories, persist]);

  const updateCategory = useCallback((id: number, data: Partial<AdminCategory>) => {
    persist(categories.map((item) => (item.id === id ? normalizeCategory({ ...item, ...data, id }, item.order) : item)));
  }, [categories, persist]);

  const deleteCategory = useCallback((id: number) => {
    persist(categories.filter((item) => item.id !== id));
  }, [categories, persist]);

  const reorder = useCallback((fromIndex: number, toIndex: number) => {
    const next = [...categories];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    persist(next.map((item, index) => ({ ...item, order: index })));
  }, [categories, persist]);

  const toggleVisible = useCallback((id: number) => {
    persist(categories.map((item) => (item.id === id ? { ...item, visible: !item.visible } : item)));
  }, [categories, persist]);

  const resetToDefault = useCallback(() => {
    persist(defaultCategories);
  }, [persist]);

  return {
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    reorder,
    toggleVisible,
    resetToDefault,
    slugify,
  };
}
